import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, of, switchMap, take, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { BillingOrganisation } from './billing-profile-api.service';
import { BillingProfileWriteBackService } from './billing-profile-writeback.service';
import {
  AcceptNegotiatedOfferRequest,
  CancellationPreview,
  CreateLicenseItem,
  CreateLicenseRequest,
  CreateLicenseResult,
  InvoiceBreakdown,
  LicenseInfo,
  LicenseTypeEnum,
  NegotiatedOffer,
  OrderDetail,
  OrderItemInfo,
  PromotionCodeInfo,
  Subscription,
  SubscriptionBilling,
  UserBillDetail,
} from '../types/rest-api';

/**
 * The organisation as query params / request fields. Empty when none is given,
 * which keeps the historical per-user behaviour.
 */
function organisationParams(
  org?: BillingOrganisation | null
): Record<string, string> {
  if (!org) return {};
  return org.kind === 'authority'
    ? { school_authority_id: org.id }
    : { school_id: org.id };
}

function organisationFields(
  org?: BillingOrganisation | null
): Partial<CreateLicenseRequest> {
  return organisationParams(org);
}

/** License lines that bundle unlimited job postings (vs. free tier or one-off ads). */
const UNLIMITED_JOB_POSTING_LICENSE_TYPES: ReadonlySet<LicenseTypeEnum> =
  new Set([
    'KANTON_SUBSCRIPTION',
    'SCHOOL_AUTHORITY_SUBSCRIPTION',
    'SCHOOL_UNIT_SUBSCRIPTION',
    'SCHOOL_UNIT_ADDITION_SUBSCRIPTION',
  ]);
import {
  loadMunicipalityPlanSelection,
  type MunicipalityPlanSelection,
} from '../pages/onboarding/municipality-plan-selection';

/**
 * License / subscription orders (OpenAPI tag: License).
 * Creating a school unit requires an existing entitlement; the backend ties that
 * to a `SCHOOL_UNIT_SUBSCRIPTION` license order.
 */
@Injectable({
  providedIn: 'root',
})
export class LicenseApiService {
  private readonly http = inject(HttpClient);
  private readonly billingWriteBack = inject(BillingProfileWriteBackService);
  private readonly baseUrl = `${environment.apiBaseUrl}/v1/licenses`;

  getAvailableLicenses(org?: BillingOrganisation | null) {
    return this.http.get<LicenseInfo[]>(this.baseUrl, {
      params: organisationParams(org),
    });
  }

  /**
   * @param org When given, returns what the ORGANISATION has bought rather than
   * what the caller personally bought — that is what lets a second Super Admin
   * see the plan at all. Omit it to ask "what have I bought?", which is still
   * what onboarding needs.
   */
  getOrders(org?: BillingOrganisation | null) {
    return this.http.get<OrderDetail[]>(`${this.baseUrl}/orders`, {
      params: organisationParams(org),
    });
  }

  getSubscriptions(
    org?: BillingOrganisation | null
  ): Observable<Subscription[]> {
    return this.http.get<Subscription[]>(`${this.baseUrl}/subscriptions`, {
      params: organisationParams(org),
    });
  }

  getCancellationPreview(orderItemId: string): Observable<CancellationPreview> {
    return this.http.get<CancellationPreview>(
      `${this.baseUrl}/orders/items/${orderItemId}/cancellation-preview`
    );
  }

  /**
   * Live Stripe invoice + proration history for an order (newest first).
   * Lazily fetched when an order row is expanded.
   */
  getOrderInvoices(orderId: string): Observable<InvoiceBreakdown[]> {
    return this.http.get<InvoiceBreakdown[]>(
      `${this.baseUrl}/orders/${orderId}/invoices`
    );
  }

  getSubscriptionBilling(
    subscriptionId: string
  ): Observable<SubscriptionBilling> {
    return this.http.get<SubscriptionBilling>(
      `${this.baseUrl}/subscriptions/${subscriptionId}/billing`
    );
  }

  cancelSubscription(orderItemId: string): Observable<void> {
    return this.http
      .put<unknown>(
        `${this.baseUrl}/orders/items/${orderItemId}/cancel-subscription`,
        {}
      )
      .pipe(map(() => undefined));
  }

  uncancelSubscription(subscriptionId: string): Observable<void> {
    return this.http
      .put<unknown>(
        `${this.baseUrl}/subscriptions/${subscriptionId}/uncancel`,
        {}
      )
      .pipe(map(() => undefined));
  }

  /**
   * @param idempotencyKey Stable key for one purchase attempt. Reuse the same key
   * across retries of the same intent so the backend replays the result instead of
   * creating (and charging) a second order. Generate a fresh key per new purchase.
   */
  /**
   * @param org The organisation being bought for. Given, the order lands on its
   * billing account and its Stripe customer instead of the purchaser's. Omitted
   * — as in onboarding, where the school does not exist yet — the historical
   * per-user path runs unchanged.
   */
  createOrder(
    body: CreateLicenseRequest,
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ) {
    return this.http
      .post<CreateLicenseResult>(
        `${this.baseUrl}/orders`,
        { ...body, ...organisationFields(org) },
        {
          headers: idempotencyKey ? { 'Idempotency-Key': idempotencyKey } : {},
        }
      )
      .pipe(
        // Every purchase in the app passes through here, which is why the
        // address is kept here rather than in each dialog. On success only: an
        // address typed for an order that failed belongs to no organisation.
        tap(() => this.billingWriteBack.afterOrder(body.bill_detail, org))
      );
  }

  getNegotiatedOffer(): Observable<NegotiatedOffer | null> {
    return this.http.get<NegotiatedOffer | null>(
      `${this.baseUrl}/negotiated-offer`
    );
  }

  acceptNegotiatedOffer(
    dealId: string,
    billDetail: UserBillDetail
  ): Observable<CreateLicenseResult> {
    const body: AcceptNegotiatedOfferRequest = {
      deal_id: dealId,
      bill_detail: billDetail,
    };
    return this.http
      .post<CreateLicenseResult>(`${this.baseUrl}/orders/negotiated`, body)
      .pipe(tap(() => this.billingWriteBack.afterOrder(billDetail)));
  }

  /**
   * Purchases one additional admin slot (USER_ADDITION_SUBSCRIPTION).
   * The backend creates a new license order item; entitlements update afterwards.
   */
  purchaseAdditionalAdminSlot(
    billDetail: CreateLicenseRequest['bill_detail'],
    quantity = 1,
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<void> {
    return this.getAvailableLicenses(org).pipe(
      take(1),
      switchMap(licenses => {
        const lic = licenses.find(
          l => l.license_type === 'USER_ADDITION_SUBSCRIPTION'
        );
        if (!lic) {
          throw new Error(
            'Keine Admin-Zusatzlizenz verfügbar. Bitte später erneut versuchen.'
          );
        }

        const item: CreateLicenseItem = {
          license_id: lic.id,
          quantity,
          ...(lic.is_subscription_based
            ? { interval: 'YEARLY' as const, interval_count: 1 }
            : {}),
        };

        return this.createOrder(
          {
            items: [item],
            bill_detail: billDetail,
          },
          idempotencyKey,
          org
        ).pipe(map(() => undefined));
      })
    );
  }

  /**
   * Purchases additional school-unit credits (SCHOOL_UNIT_ADDITION_SUBSCRIPTION).
   * Used when the user needs to add more school units than their plan includes.
   */
  purchaseAdditionalSchoolUnitSlots(
    billDetail: CreateLicenseRequest['bill_detail'],
    quantity = 1,
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<void> {
    return this.getAvailableLicenses(org).pipe(
      take(1),
      switchMap(licenses => {
        const lic = licenses.find(
          l => l.license_type === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION'
        );
        if (!lic) {
          throw new Error(
            'Keine Schuleinheit-Zusatzlizenz verfügbar. Bitte später erneut versuchen.'
          );
        }

        const item: CreateLicenseItem = {
          license_id: lic.id,
          quantity,
          ...(lic.is_subscription_based
            ? { interval: 'YEARLY' as const, interval_count: 1 }
            : {}),
        };

        return this.createOrder(
          {
            items: [item],
            bill_detail: billDetail,
          },
          idempotencyKey,
          org
        ).pipe(map(() => undefined));
      })
    );
  }

  /**
   * The numeric `order_number` of an order the user already placed, for
   * {@link SchoolApiService.createSchool}. Never orders: unlike
   * {@link resolveOrderNumberForCreateSchool}, a missing order is an error, so a
   * retry can never buy a second subscription.
   */
  findOrderNumberForCreateSchool(): Observable<number> {
    return this.getOrders().pipe(
      map(orders => {
        const n = this.pickOrderNumberForCreateSchool(orders);
        if (n == null) {
          throw new Error(
            'Keine Lizenzbestellung gefunden. Bitte kontaktiere unseren Support.'
          );
        }
        return n;
      })
    );
  }

  /**
   * Resolves the numeric `order_number` for {@link SchoolApiService.createSchool}.
   * The API expects a JSON **integer**, not the order UUID string (`order.id`).
   *
   * Flow:
   * 1. `GET /v1/licenses/orders`
   * 2. If no suitable order: `POST /v1/licenses/orders` via {@link ensureSchoolUnitSubscriptionEntitlement}
   * 3. `GET /v1/licenses/orders` again, then pick a numeric `order_number`
   *
   * Prefer `order.order_number` from the orders payload when the backend exposes it.
   * Otherwise derive a stable positive integer from `order.id` (until the API always returns `order_number`).
   */
  resolveOrderNumberForCreateSchool(
    billDetail: CreateLicenseRequest['bill_detail'],
    options?: { ensureSubscriptionIfMissing?: boolean },
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<number> {
    const ensure = options?.ensureSubscriptionIfMissing ?? true;
    return this.getOrders().pipe(
      switchMap(orders => {
        const n = this.pickOrderNumberForCreateSchool(orders);
        if (n != null) {
          return of(n);
        }
        if (!ensure) {
          return throwError(
            () =>
              new Error(
                'Keine Lizenzbestellung gefunden. Bitte schliesse zuerst die Bestellung ab.'
              )
          );
        }
        return this.ensureSchoolUnitSubscriptionEntitlement(
          billDetail,
          idempotencyKey,
          org
        ).pipe(
          switchMap(() => this.getOrders()),
          map(orders2 => {
            const n2 = this.pickOrderNumberForCreateSchool(orders2);
            if (n2 != null) {
              return n2;
            }
            throw new Error(
              'Keine passende Lizenzbestellung gefunden. Bitte später erneut versuchen.'
            );
          })
        );
      })
    );
  }

  /**
   * Resolves the root `order_number` plus a positional `order_number` per Standort for
   * POST /v1/schools/create.
   *
   * `sub_schools[].order_number` is the **position** of the Standort within the parent
   * (`SubSchoolCreateReq.order_number` in the backend) — not a license-order reference.
   * The backend validator requires exactly `[1, 2, …, N]` (unique, no gaps, starting at 1),
   * so we always return that sequence regardless of how many license orders exist.
   *
   * Ensures a SCHOOL_UNIT_SUBSCRIPTION entitlement when none is present yet.
   */
  resolveOrderNumbersForSchoolCreate(
    subSchoolCount: number,
    billDetail: CreateLicenseRequest['bill_detail'],
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<{ root: number; subSchoolOrderNumbers: number[] }> {
    return this.getOrders().pipe(
      switchMap(orders => {
        if (this.pickOrderNumberForCreateSchool(orders) == null) {
          return this.ensureSchoolUnitSubscriptionEntitlement(
            billDetail,
            idempotencyKey,
            org
          ).pipe(switchMap(() => this.getOrders()));
        }
        return of(orders);
      }),
      map(orders => this.buildOrderNumbersPayload(orders, subSchoolCount))
    );
  }

  private buildOrderNumbersPayload(
    orders: OrderDetail[],
    subSchoolCount: number
  ): { root: number; subSchoolOrderNumbers: number[] } {
    const root = this.pickOrderNumberForCreateSchool(orders);
    if (root == null) {
      throw new Error(
        'Keine passende Lizenzbestellung gefunden. Bitte später erneut versuchen.'
      );
    }
    const subSchoolOrderNumbers = Array.from(
      { length: subSchoolCount },
      (_, i) => i + 1
    );
    return { root, subSchoolOrderNumbers };
  }

  private pickOrderNumberForCreateSchool(orders: OrderDetail[]): number | null {
    const preferred: LicenseTypeEnum[] = [
      'SCHOOL_AUTHORITY_SUBSCRIPTION',
      'SCHOOL_UNIT_ADDITION_SUBSCRIPTION',
      'SUB_SCHOOL_ADDITION_SUBSCRIPTION',
      'SCHOOL_UNIT_SUBSCRIPTION',
      'KANTON_SUBSCRIPTION',
    ];
    for (const licenseType of preferred) {
      for (const order of orders) {
        for (const item of order.items ?? []) {
          if (item.is_cancelled) continue;
          if (item.license?.license_type === licenseType) {
            return this.coerceOrderNumber(order, orders);
          }
        }
      }
    }
    return null;
  }

  /**
   * Backend expects a JSON integer `order_number` (not UUID `id`).
   * Prefer `order.order_number` from the API; else 1-based index in the orders list; else stable int from id.
   */
  private coerceOrderNumber(order: OrderDetail, orders: OrderDetail[]): number {
    if (
      typeof order.order_number === 'number' &&
      Number.isFinite(order.order_number)
    ) {
      return order.order_number;
    }
    const raw = order as OrderDetail & { number?: number };
    if (typeof raw.number === 'number' && Number.isFinite(raw.number)) {
      return raw.number;
    }
    const idx = orders.indexOf(order);
    if (idx >= 0) {
      return idx + 1;
    }
    const hex = order.id.replace(/-/g, '').slice(0, 8);
    const unsigned = parseInt(hex, 16);
    return (unsigned % 2147483646) + 1;
  }

  /**
   * Ensures the current user has a non-cancelled school-unit subscription order item
   * so {@link SchoolApiService.createSchool} can succeed. Idempotent when an order already exists.
   */
  ensureSchoolUnitSubscriptionEntitlement(
    billDetail: CreateLicenseRequest['bill_detail'],
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<void> {
    return this.getOrders(org).pipe(
      switchMap(orders => {
        if (this.hasActiveSchoolUnitOrder(orders)) {
          return of(undefined);
        }
        return this.getAvailableLicenses(org).pipe(
          take(1),
          switchMap(licenses => {
            const lic = licenses.find(
              l => l.license_type === 'SCHOOL_UNIT_SUBSCRIPTION'
            );
            if (!lic) {
              throw new Error(
                'Keine Schuleinheit-Lizenz verfügbar. Bitte später erneut versuchen.'
              );
            }
            const item: CreateLicenseItem = {
              license_id: lic.id,
              quantity: 1,
              ...(lic.is_subscription_based
                ? { interval: 'YEARLY' as const, interval_count: 1 }
                : {}),
            };
            return this.createOrder(
              {
                items: [item],
                bill_detail: billDetail,
              },
              idempotencyKey,
              org
            ).pipe(map(() => undefined));
          })
        );
      })
    );
  }

  /**
   * Fetches the USER_ADDITION_SUBSCRIPTION license info (for pricing display).
   * Returns the LicenseInfo or null if not available.
   */
  getUserAdditionLicenseInfo(): Observable<LicenseInfo | null> {
    return this.getAvailableLicenses().pipe(
      map(
        licenses =>
          licenses.find(l => l.license_type === 'USER_ADDITION_SUBSCRIPTION') ??
          null
      )
    );
  }

  /**
   * Fetches the SCHOOL_UNIT_ADDITION_SUBSCRIPTION license info (for pricing display).
   * Returns the LicenseInfo or null if not available.
   */
  getSchoolUnitAdditionLicenseInfo(): Observable<LicenseInfo | null> {
    return this.getAvailableLicenses().pipe(
      map(
        licenses =>
          licenses.find(
            l => l.license_type === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION'
          ) ?? null
      )
    );
  }

  /**
   * Resolves `license_type` from an order line; tolerates API variants where `license` is flattened.
   */
  private orderItemLicenseType(
    it: OrderItemInfo & { license_type?: LicenseTypeEnum }
  ): LicenseTypeEnum | undefined {
    return it.license?.license_type ?? it.license_type;
  }

  /**
   * License lines that grant **unlimited** job postings **without** consulting school
   * `authorized_for_modules.jobs` (Kanton / Schulgemeinde / extra Schuleinheit slot).
   *
   * A bare {@link LicenseTypeEnum.SCHOOL_UNIT_SUBSCRIPTION} is the normal onboarding
   * entitlement for a single Schuleinheit — including the **free** tier — and must
   * **not** be treated as unlimited here; unlimited for paid Schuleinheit uses
   * `GET /v1/schools/…/detail` → `authorized_for_modules.jobs === true`.
   */
  hasBundledUnlimitedJobPostingLines(orders: OrderDetail[]): boolean {
    return this.bundledUnlimitedJobPostingLines(orders).length > 0;
  }

  /** The matching lines, so a caller can report WHICH license granted unlimited. */
  bundledUnlimitedJobPostingLines(
    orders: OrderDetail[]
  ): { orderId: string; licenseType: LicenseTypeEnum }[] {
    const found: { orderId: string; licenseType: LicenseTypeEnum }[] = [];
    for (const o of orders) {
      for (const it of o.items ?? []) {
        if (it.is_cancelled) continue;
        const t = this.orderItemLicenseType(it);
        if (
          t === 'KANTON_SUBSCRIPTION' ||
          t === 'SCHOOL_AUTHORITY_SUBSCRIPTION' ||
          t === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION'
        ) {
          found.push({ orderId: o.id, licenseType: t });
        }
      }
    }
    return found;
  }

  /**
   * True when the user has an active subscription order that includes bundled job postings
   * (not the free-only tier without a Schuleinheit / authority / kanton line).
   */
  hasActiveUnlimitedJobPostingSubscription(orders: OrderDetail[]): boolean {
    for (const o of orders) {
      for (const it of o.items ?? []) {
        if (it.is_cancelled) continue;
        const t = this.orderItemLicenseType(it);
        if (t && UNLIMITED_JOB_POSTING_LICENSE_TYPES.has(t)) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Extra job postings purchased as one-off products (adds to the single free slot when
   * there is no unlimited subscription).
   */
  countPurchasedSingleJobPostingSlots(orders: OrderDetail[]): number {
    let n = 0;
    for (const o of orders) {
      for (const it of o.items ?? []) {
        if (it.is_cancelled) continue;
        const t = this.orderItemLicenseType(it);
        if (t === 'SUBSTITUTION_JOB_POSTING' || t === 'PERMANENT_JOB_POSTING') {
          n += it.quantity ?? 0;
        }
      }
    }
    return n;
  }

  private hasActiveSchoolUnitOrder(orders: OrderDetail[]): boolean {
    for (const o of orders) {
      for (const it of o.items ?? []) {
        if (it.is_cancelled) continue;
        if (this.orderItemLicenseType(it) === 'SCHOOL_UNIT_SUBSCRIPTION') {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * True when the current user already has an active Schuleinheit subscription line.
   * Uses `GET /v1/licenses/orders` and checks for a non-cancelled `SCHOOL_UNIT_SUBSCRIPTION`.
   */
  hasActiveSchoolUnitSubscription(): Observable<boolean> {
    return this.getOrders().pipe(
      map(orders => this.hasActiveSchoolUnitOrder(orders))
    );
  }

  /**
   * Signals beyond a minimal free Schuleinheit entitlement (add-ons, upgraded term,
   * multiple billed orders/lines, or a real Stripe `transaction_id`). Used with school
   * `authorized_for_modules.jobs` so the sidebar can hide «Upgrade auf Pro» when `jobs`
   * is still unset after checkout — and even on plain 1-year/single-school paid plans
   * whose order shape is otherwise indistinguishable from the free entitlement.
   */
  hasSchuleinheitPaidPlanEvidence(orders: OrderDetail[]): boolean {
    let ordersWithActiveItems = 0;
    let schoolUnitSubLines = 0;
    for (const order of orders) {
      const active = (order.items ?? []).filter(it => !it.is_cancelled);
      if (active.length === 0) continue;
      ordersWithActiveItems++;

      if (active.length > 1) return true;

      const hasSchoolUnitLine = active.some(
        it => this.orderItemLicenseType(it) === 'SCHOOL_UNIT_SUBSCRIPTION'
      );
      // Paid Stripe checkouts populate `transaction_id`; free entitlements written via
      // `ensureSchoolUnitSubscriptionEntitlement` go through POST /v1/licenses/orders
      // without Stripe and leave it as an empty/placeholder string.
      if (hasSchoolUnitLine && this.isPaidTransactionId(order.transaction_id)) {
        return true;
      }

      for (const it of active) {
        const t = this.orderItemLicenseType(it);
        if (t === 'SCHOOL_UNIT_SUBSCRIPTION') {
          schoolUnitSubLines++;
        }
        if (
          t === 'USER_ADDITION_SUBSCRIPTION' ||
          t === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION' ||
          t === 'SUB_SCHOOL_ADDITION_SUBSCRIPTION'
        ) {
          return true;
        }
        if (t === 'SCHOOL_UNIT_SUBSCRIPTION') {
          if (it.is_updated) return true;
          if (it.interval_count != null && it.interval_count > 1) return true;
        }
      }
    }
    if (schoolUnitSubLines > 1) return true;
    return ordersWithActiveItems > 1;
  }

  /**
   * Stripe transaction ids are non-empty and longer than a few chars; free orders carry
   * an empty/placeholder string. Conservative — short or all-zero ids count as not-paid.
   */
  private isPaidTransactionId(
    transactionId: string | null | undefined
  ): boolean {
    const t = (transactionId ?? '').trim();
    if (t.length < 6) return false;
    if (/^0+$/.test(t)) return false;
    return true;
  }

  /**
   * True when `GET /v1/licenses/orders` shows paid school-profile entitlements: Kanton /
   * Schulgemeinde / Schuleinheit add-on lines, or Schuleinheit checkout evidence beyond the
   * minimal free row (mirrors {@link FreePlanJobPostingService.maxJobPostingsAllowed}).
   */
  hasPaidSchoolProfileEntitlementFromOrders(orders: OrderDetail[]): boolean {
    return (
      this.hasBundledUnlimitedJobPostingLines(orders) ||
      this.hasSchuleinheitPaidPlanEvidence(orders)
    );
  }

  private hasActiveSchoolAuthorityOrder(orders: OrderDetail[]): boolean {
    for (const o of orders) {
      for (const it of o.items ?? []) {
        if (it.is_cancelled) continue;
        if (this.orderItemLicenseType(it) === 'SCHOOL_AUTHORITY_SUBSCRIPTION') {
          return true;
        }
      }
    }
    return false;
  }

  createSchoolAuthorityPlanOrdersFromSession(
    billDetail: CreateLicenseRequest['bill_detail'],
    promotionCode?: string | null,
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<CreateLicenseResult> {
    const sel = loadMunicipalityPlanSelection();
    if (!sel) {
      return throwError(
        () =>
          new Error(
            'Keine Plan-Auswahl in der Session. Bitte Plan erneut wählen.'
          )
      );
    }
    if (sel.subscriptionType === 'SCHOOL_FREE') {
      return of({ success: true });
    }
    return this.getOrders(org).pipe(
      switchMap(orders => {
        // Look for existing non-cancelled authority subscription
        if (this.hasActiveSchoolAuthorityOrder(orders)) {
          return of({ success: true });
        }
        return this.buildAuthorityPlanItems(sel, org).pipe(
          switchMap(items =>
            this.createOrder(
              {
                items,
                bill_detail: billDetail,
                ...(promotionCode ? { promotion_code: promotionCode } : {}),
              },
              idempotencyKey,
              org
            )
          )
        );
      })
    );
  }

  /**
   * Builds a Schuleinheit plan order from session.
   */
  createSchoolUnitPlanOrdersFromSession(
    billDetail: CreateLicenseRequest['bill_detail'],
    promotionCode?: string | null,
    idempotencyKey?: string,
    org?: BillingOrganisation | null
  ): Observable<CreateLicenseResult> {
    const sel = loadMunicipalityPlanSelection(); // Shared storage for now as fields are identical
    if (!sel) {
      return throwError(
        () =>
          new Error(
            'Keine Plan-Auswahl in der Session. Bitte Plan erneut wählen.'
          )
      );
    }
    if (sel.subscriptionType === 'SCHOOL_FREE') {
      return of({ success: true });
    }
    /**
     * Fachkraft → Schuleinheit upgrade: the school is created after
     * {@link LicenseApiService.ensureSchoolUnitSubscriptionEntitlement} already added a
     * Schuleinheit-Abonnement. A second POST must not repeat that line — only add-ons (e.g. admins).
     */
    return this.getOrders(org).pipe(
      switchMap(orders =>
        this.buildSchoolUnitPlanItems(sel, orders, org).pipe(
          switchMap(items => {
            if (items.length === 0) {
              return of({ success: true });
            }
            return this.createOrder(
              {
                items,
                bill_detail: billDetail,
                ...(promotionCode ? { promotion_code: promotionCode } : {}),
              },
              idempotencyKey,
              org
            );
          })
        )
      )
    );
  }

  /**
   * @param existingOrders Pass current GET /v1/licenses/orders — omit base Schuleinheit line if already entitled.
   */
  private buildSchoolUnitPlanItems(
    sel: MunicipalityPlanSelection,
    existingOrders: OrderDetail[],
    org?: BillingOrganisation | null
  ): Observable<CreateLicenseItem[]> {
    return this.getAvailableLicenses(org).pipe(
      take(1),
      map(licenses => {
        const items: CreateLicenseItem[] = [];
        const alreadyHasBase = this.hasActiveSchoolUnitOrder(existingOrders);
        const hasAuthority = this.hasActiveSchoolAuthorityOrder(existingOrders);
        // OpenAPI: users with a school authority subscription only see user addition + school unit
        // addition in GET /v1/licenses — not SCHOOL_UNIT_SUBSCRIPTION. In that case the paid
        // «Schuleinheit» checkout must order SCHOOL_UNIT_ADDITION_SUBSCRIPTION instead.
        if (!alreadyHasBase) {
          const baseLic = licenses.find(
            l => l.license_type === 'SCHOOL_UNIT_SUBSCRIPTION'
          );
          const unitAddLic = licenses.find(
            l => l.license_type === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION'
          );
          if (baseLic) {
            items.push({
              license_id: baseLic.id,
              quantity: 1,
              ...(baseLic.is_subscription_based
                ? {
                    interval: 'YEARLY' as const,
                    interval_count: sel.durationYears,
                  }
                : {}),
            });
          } else if (hasAuthority && unitAddLic) {
            items.push({
              license_id: unitAddLic.id,
              quantity: 1,
              ...(unitAddLic.is_subscription_based
                ? {
                    interval: 'YEARLY' as const,
                    interval_count: 1,
                  }
                : {}),
            });
          } else {
            throw new Error(
              'Keine Schuleinheit-Lizenz im Katalog. Bitte später erneut versuchen.'
            );
          }
        }

        const adminExtra = licenses.find(
          l => l.license_type === 'USER_ADDITION_SUBSCRIPTION'
        );
        if (sel.extraAdmins > 0 && adminExtra) {
          items.push({
            license_id: adminExtra.id,
            quantity: sel.extraAdmins,
            ...(adminExtra.is_subscription_based
              ? {
                  interval: 'YEARLY' as const,
                  interval_count: 1,
                }
              : {}),
          });
        }
        return items;
      })
    );
  }

  private buildAuthorityPlanItems(
    sel: MunicipalityPlanSelection,
    org?: BillingOrganisation | null
  ): Observable<CreateLicenseItem[]> {
    return this.getAvailableLicenses(org).pipe(
      take(1),
      map(licenses => {
        const items: CreateLicenseItem[] = [];
        const auth = licenses.find(
          l => l.license_type === 'SCHOOL_AUTHORITY_SUBSCRIPTION'
        );
        if (!auth) {
          throw new Error(
            'Keine Schulgemeinde-Lizenz im Katalog. Bitte später erneut versuchen.'
          );
        }
        items.push({
          license_id: auth.id,
          quantity: 1,
          ...(auth.is_subscription_based
            ? { interval: 'YEARLY' as const, interval_count: sel.durationYears }
            : {}),
        });
        const adminExtra = licenses.find(
          l => l.license_type === 'USER_ADDITION_SUBSCRIPTION'
        );
        if (sel.extraAdmins > 0 && adminExtra) {
          items.push({
            license_id: adminExtra.id,
            quantity: sel.extraAdmins,
            // Backend: interval_count must be 1 for USER_ADDITION_SUBSCRIPTION.
            ...(adminExtra.is_subscription_based
              ? {
                  interval: 'YEARLY' as const,
                  interval_count: 1,
                }
              : {}),
          });
        }
        const unitExtra = licenses.find(
          l => l.license_type === 'SCHOOL_UNIT_ADDITION_SUBSCRIPTION'
        );
        const extraUnits = Math.max(0, sel.totalSchoolUnits - 1);
        if (extraUnits > 0 && unitExtra) {
          items.push({
            license_id: unitExtra.id,
            quantity: extraUnits,
            // Matches purchaseAdditionalSchoolUnitSlots — add-on aligns to main subscription term via backend.
            ...(unitExtra.is_subscription_based
              ? {
                  interval: 'YEARLY' as const,
                  interval_count: 1,
                }
              : {}),
          });
        }
        return items;
      })
    );
  }

  validatePromotionCode(code: string): Observable<PromotionCodeInfo> {
    return this.http.post<PromotionCodeInfo>(
      `${this.baseUrl}/validate-promotion-code`,
      { code }
    );
  }
}
